// src/PreferitiPage.jsx — Pagina preferiti del coordinatore — griglia card
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Chip, CircularProgress, IconButton, Tooltip, Stack
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { fetchJson, authHeaders } from './api';

function resolveImg(u) {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  return `${window.location.origin}${u.startsWith('/') ? '' : '/'}${u}`;
}

function StatusDot({ available }) {
  return (
    <Box sx={{
      width: 8, height: 8, borderRadius: '50%',
      bgcolor: available ? '#4ade80' : '#f87171',
      flexShrink: 0
    }} />
  );
}

export default function PreferitiPage({ token }) {
  const navigate = useNavigate();
  const [preferiti, setPreferiti] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await fetchJson('/favorites', { headers: authHeaders(token) });
        setPreferiti(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const remove = async (e, freelancerId) => {
    e.stopPropagation();
    try {
      await fetchJson(`/favorites/${freelancerId}`, { method: 'DELETE', headers: authHeaders(token) });
      setPreferiti(prev => prev.filter(p => p.freelancer_id !== freelancerId));
    } catch (e) { console.error(e); }
  };

  const open = (id) => navigate(`/freelancer/${id}`);

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 4 }}>
        <StarIcon sx={{ color: '#d4a550', fontSize: 22 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#f0ece4' }}>
          Preferiti
        </Typography>
        {!loading && (
          <Chip label={`${preferiti.length}`} size="small"
            sx={{ bgcolor: 'rgba(212,165,80,0.1)', border: '1px solid rgba(212,165,80,0.3)', color: '#d4a550', fontWeight: 700 }} />
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#d4a550' }} />
        </Box>
      ) : preferiti.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <StarBorderIcon sx={{ fontSize: 52, color: '#2a2a2a', mb: 2 }} />
          <Typography sx={{ color: '#555', fontSize: 14 }}>
            Nessun freelance nei preferiti.<br />
            Aggiungili dal loro profilo.
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 2
        }}>
          {preferiti.map(p => {
            const imgUrl = resolveImg(p.profile_image_url);
            const initials = `${p.nome?.[0] ?? ''}${p.cognome?.[0] ?? ''}`.toUpperCase() || '?';

            return (
              <Box
                key={p.freelancer_id}
                onClick={() => open(p.freelancer_table_id || p.freelancer_id)}
                sx={{
                  borderRadius: 3, overflow: 'hidden', cursor: 'pointer',
                  bgcolor: '#13161c', border: '1px solid rgba(255,255,255,0.06)',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: 'rgba(212,165,80,0.35)', transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }
                }}
              >
                {/* Foto */}
                <Box sx={{ position: 'relative', width: '100%', paddingTop: '75%', bgcolor: '#1a1d23' }}>
                  {imgUrl ? (
                    <Box component="img" src={imgUrl} alt={`${p.nome} ${p.cognome}`}
                      sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Box sx={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'linear-gradient(135deg, #1e1d23, #2a2520)',
                      fontSize: 36, fontWeight: 700, color: '#d4a550', fontFamily: 'Kanit, sans-serif'
                    }}>
                      {initials}
                    </Box>
                  )}
                  {/* Rimuovi btn */}
                  <Tooltip title="Rimuovi dai preferiti">
                    <IconButton size="small" onClick={e => remove(e, p.freelancer_id)}
                      sx={{
                        position: 'absolute', top: 8, right: 8,
                        bgcolor: 'rgba(0,0,0,0.6)', color: '#d4a550',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.85)', color: '#f87171' }
                      }}>
                      <StarIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Info */}
                <Box sx={{ p: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, color: '#f0ece4', fontSize: 14, mb: 0.3 }}>
                    {p.nome} {p.cognome}
                  </Typography>
                  {p.ruolo && (
                    <Stack direction="row" alignItems="center" spacing={0.8}>
                      <StatusDot available={p.is_available} />
                      <Typography sx={{ color: '#888', fontSize: 12 }}>{p.ruolo}</Typography>
                    </Stack>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
